import {
    Alert,
    Col,
    DatePicker,
    Form,
    message,
    Row,
    Select,
    Table,
  } from "antd";
  import { ColumnsType } from "antd/lib/table";
  import { apis, AUTH_HEADER_NAME } from "common/apis";
  import { IPickerMappingUpload, IPickerMappingUploadRow } from "common/interface/IPickerUploadScheme";
  import { routes } from "common/routes";
  import { generateHeaders } from "common/utils";
  import { AppPageHeader } from "components/AppPageHeader";
  import { AppUpload } from "components/AppUpload";
  import { useAppDispatch } from "hooks/useAppDispatch";
  import { useAppSelector } from "hooks/useAppSelector";
  import { useDocumentTitle } from "hooks/usePageTitle";
import moment from "moment";
  import { useState, useEffect, useMemo } from "react";
  import { useNavigate } from "react-router-dom";
import { removeProcessingUserDay, setProcessingUserDay } from "store/admin-slice/adminServicesSlice";
  
  export const UploadUserDayMappingPage = () => {
    useDocumentTitle("Upload User Day Mapping");
    const { authToken } = useAppSelector((state) => state.app.user);
    
    const { processingUserDayMapping } = useAppSelector(
      (state) => state.adminServices,
    );
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [hasErrorRows, setHasErrorRows] = useState(false);
    const [date, setDate] = useState<string>();
  
    useEffect(() => {
      if (
        processingUserDayMapping &&
        processingUserDayMapping.key
      ) {
        navigate(`/${routes.USER_SERVICES.SAVE_USER_DAY_MAPPING}`, {});
      }
  
      return () => {
        message.destroy();
      };
    }, [processingUserDayMapping, navigate]);
  
    useEffect(() => {
      const failed = processingUserDayMapping?.failedRows?.length;
  
      if (failed) {
        setHasErrorRows(true);
      }
    }, [processingUserDayMapping?.failedRows]);
  
    const headers: { [name: string]: string } = {
      ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
    };
  
    const onUploaded = (data: IPickerMappingUpload) => {
      dispatch(setProcessingUserDay(data));
    };
  
    const onUploading = () => {
      dispatch(removeProcessingUserDay());
    };
  
    const { headerMapping } = processingUserDayMapping;
    const headerMappingWithError = headerMapping + ",errors:Errors";
  
    const generateMemoizedHeaders = useMemo(
      () => generateHeaders(headerMappingWithError),
      [headerMappingWithError]
    );
  
    const tableHeaders = generateMemoizedHeaders || {};
    const successRows = processingUserDayMapping?.successRows || [];
    const failedRows = processingUserDayMapping?.failedRows || [];
    const successData = successRows?.map((row, i) => ({
      ...row,
      key: `data-success-${i}`,
    }));
    const failedData = failedRows?.map((row, i) => ({
      ...row,
      key: `data-failed-${i}`,
    }));
    const data = [...failedData, ...successData];
    const columns: ColumnsType<
      IPickerMappingUploadRow & { key: string }
    > = [
        ...Object.keys(tableHeaders).map((key) => ({
          key,
          title: tableHeaders[key],
          dataIndex: key,
          render: (
            text: string,
            record: IPickerMappingUploadRow & { key: string }
          ) => {
            if (key === "errors") {
              const errorMessages = record.errors
                ?.map((error) => error.message)
                .join(", ");
              return <>{errorMessages}</>;
            }
            return <>{text}</>;
          },
        })),
      ];
  
    const addBorderToErrorRows = (
      record: IPickerMappingUploadRow & { key: string }
    ) => {
      if (record.errors && record.errors.length) {
        return "error-row";
      }
  
      return "";
    };
  
    return (
      <>
        <Row>
          <Col>
            <AppPageHeader title="Upload User Day Mapping" />
          </Col>
        </Row>
        <Row>
          <Form>
            <Form.Item label="Select date" name="date" required>
              <DatePicker
              disabledDate={(current) => {
                return current < moment(moment().subtract(moment.duration(1, "days")), "YYYY-MM-DD");
              }}
                onChange={(val) => setDate(val?.format("yyyy-MM-DD"))}
              />
            </Form.Item>
          </Form>
        </Row>
        {(date) && (
          <Row>
            <Col lg={16} xs={24}>
              <AppUpload
                uploadUrl={apis.uploadUserDayMapping}
                headers={headers}
                onUploaded={onUploaded}
                onUploading={onUploading}
                hasErrorRows={hasErrorRows}
                data={{ date }}
              />
            </Col>
          </Row>
        )}
        {Object.keys(processingUserDayMapping).length != 0 &&
          !processingUserDayMapping.key && (
            <>
              <Row>
                <Col lg={16} xs={24}>
                  <Alert
                    type="error"
                    message="Errors in sheet data."
                    description="Please correct errors and upload again."
                    showIcon
                    banner
                  />
                </Col>
              </Row>
  
              <Row>
                <Table
                    size="small"
                    columns={columns}
                    dataSource={data}
                    rowClassName={(record) => addBorderToErrorRows(record)}
                    scroll={{ y: 500 }}
                    className="table-with-errors"
                  ></Table>
              </Row>
            </>
          )}
      </>
    );
  };
  